import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../../utils/api';
import { Modal, Field, Input, Select, Switch, Button, Banner, useLoad, errMsg, todayIso, hoursOf } from '../kit';
import { flagText, roundMinutes } from './TimeEntryForm';

// Communications time: a call, meeting, email or SMS in the Communications log can be logged as a
// time entry. It then counts in SC hours and claims like any other entry.
const toMin = (t) => { if (!t || !/^\d{1,2}:\d{2}/.test(t)) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };

export default function CommTimeModal({ comm, onClose, onSaved }) {
  const open = !!comm;
  const types = useLoad('/sc/activity-types', null, { skip: !open });
  const me = useLoad('/sc/me', null, { skip: !open });
  const rounding = me.data?.settings?.rounding_minutes || 1;
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!comm) return;
    setF({ note_date: String(comm.comm_date || comm.created_at || todayIso()).slice(0, 10), start_time: '', end_time: '', activity_type_id: '', billable: undefined });
  }, [comm]);
  // Calls and meetings are usually direct service; emails and SMS are non-face-to-face.
  const list = useMemo(() => types.data || [], [types.data]);
  useEffect(() => {
    if (!comm || !list.length) return;
    const want = ['email', 'sms', 'portal'].includes(comm.channel) ? 'NF2F' : '';
    const t = list.find((x) => x.claim_type === want && x.billable_default) || list[0];
    setF((p) => (p.activity_type_id ? p : { ...p, activity_type_id: t.id }));
  }, [comm, list]);
  const type = list.find((t) => t.id === f.activity_type_id);
  const billable = f.billable === undefined ? (type ? type.billable_default !== false : true) : f.billable;
  const raw = (() => { const a = toMin(f.start_time); const b = toMin(f.end_time); return a != null && b != null && b > a ? b - a : null; })();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v && v.target ? v.target.value : v }));

  const save = async () => {
    if (!f.start_time || !f.end_time || raw == null) return toast.error('Enter a start and an end time (the end after the start).');
    setBusy(true);
    try {
      const r = await api.post(`/sc/communications/${comm.id}/time`, { note_date: f.note_date, start_time: f.start_time, end_time: f.end_time, activity_type_id: f.activity_type_id, billable });
      toast.success(`Time logged · ${hoursOf(r.data?.entry?.minutes_rounded)} h`);
      if ((r.data?.flags || []).length) toast(r.data.flags.map(flagText).join(' · '), { icon: '⚑', duration: 6000 });
      // The same signal the Log time sheet sends, so Notes & Billing and SC hours refresh.
      try { window.dispatchEvent(new CustomEvent('sc:time-saved', { detail: r.data?.entry || null })); } catch { /* old browsers */ }
      onSaved?.(r.data?.entry);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log time for this communication" sub={comm ? [comm.subject, comm.contact_name].filter(Boolean).join(' · ') : ''}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={save}>Log time</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Date"><Input type="date" value={f.note_date || ''} max={todayIso()} onChange={set('note_date')} /></Field>
          <Field label="Start"><Input type="time" value={f.start_time || ''} onChange={set('start_time')} /></Field>
          <Field label="End"><Input type="time" value={f.end_time || ''} onChange={set('end_time')} /></Field>
        </div>
        {raw != null && <p className="text-[12.5px] text-violet-700 font-medium tabular-nums">{raw} min worked, {hoursOf(roundMinutes(raw, rounding))} h recorded</p>}
        <Field label="Activity type"><Select value={f.activity_type_id || ''} onChange={(v) => setF((p) => ({ ...p, activity_type_id: v, billable: undefined }))}
          options={list.map((t) => ({ value: t.id, label: t.name }))} /></Field>
        <Switch label="Billable" checked={billable} onChange={(v) => setF((p) => ({ ...p, billable: v }))} />
        <Banner tone="blue">The communication's subject and text become the note. Edit it later in Notes &amp; Billing.</Banner>
      </div>
    </Modal>
  );
}
